<script>
  import { onMount } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faUpload, faTrashCan, faDownload, faRotate, faCloudArrowUp, faCloudArrowDown, faXmark, faEye, faEllipsis, faRightToBracket, faBinaryLock, faClipboard, faFileImport } from '@fortawesome/pro-light-svg-icons';
  // QA-012: AWS SDK removed from the SPA bundle. All S3 ops go through the
  // backend FileProxyController so credentials never reach the browser.
  import { userRole, isReadOnly } from '../stores/sessionStore';
  import { selectedStudy, selectedSite, selectedParticipant } from '../stores/selectionStore';
  import { isFeatureEnabled, studyProperties } from '../stores/configStore';
  import './styles/icon-button.css';
  import { createEventDispatcher } from 'svelte';
  import Pagination from './Pagination.svelte';
  import SiteCgmExportModal from './SiteCgmExportModal.svelte';
  import * as XLSX from 'xlsx';
  const dispatch = createEventDispatcher();

  // File type definitions
  const FILE_TYPES = {
    ALL: { code: 'all', label: 'All' },
    REPORTS: { code: 'reports', label: 'Report' },
    TRAINING: { code: 'training', label: 'Training' },
    SENSOR: { code: 'sensor', label: 'Sensor' },
    OTHER: { code: 'other', label: 'Other' },
    EXPORTS: { code: 'exports', label: 'Export' }
  };

  // Media file extensions
  const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
  const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.wmv'];
  const PDF_EXTENSIONS = ['.pdf'];
  const OFFICE_EXTENSIONS = {
    EXCEL: ['.xlsx', '.xls', '.xlsm', '.xlsb'],
    POWERPOINT: ['.pptx', '.ppt', '.pps', '.ppsx']
  };

  // Props - only keep what we need
  export let title = 'Files';
  export let onClose = () => {}; // Callback for when close button is clicked

  let files = [];
  let isLoading = false;
  let uploadProgress = 0;
  let error = null;
  let fileInput;
  let selectedFileType = FILE_TYPES.ALL.code;
  let isInitialized = false;
  let showPreview = false;
  let previewUrl = '';
  let previewType = ''; // 'image', 'video', 'pdf', 'excel', or 'powerpoint'
  let previewBlobUrl = ''; // QA-017: blob URL kept so closePreview can revoke it
  let showSiteExportModal = false;
  // Feature toggles
  const enableLegacySensorActions = false;
  // Load Sensor Data result state
  let showLoadResultModal = false;
  let loadResultData = null;
  let loadResultStatus = null;
  let loadResultOk = false;
  let loadResultError = null;
  let loadResultSummary = '';
  let showLoadRaw = false;
  let loadRequestUrl = '';
  let loadRequestPayload = null;
  // Load Sensor Data progress state
  let showLoadProgressModal = false;
  let loadProgressMessage = 'Loading sensor data…';
  
  // Upload modal state
  let showUploadModal = false;
  let selectedFile = null;
  let selectedUploadType = '';
  let selectedVisit = '';
  let visitOptions = [];
  let timezoneOptions = [];
  let isDragOver = false;
  // Controls whether to append protocol/site/subject/visit to filename
  let augmentFilenames = (import.meta.env.VITE_AUGMENT_UPLOAD_FILENAMES || 'false') === 'true';
  
  // Import modal state
  let showImportModal = false;
  let importInProgress = false;
  let importError = null;
  let importSummary = { inserted: 0, attempted: 0, duplicates: 0, filename: '' };
  
  // Overwrite warning state (integrated into upload modal)
  let showOverwriteWarning = false;
  let conflictingFile = null;
  let suggestedFilename = '';

  // Load Sensor Data configuration modal state
  let showLoadConfigModal = false;
  let loadConfigFile = null;
  let selectedLoadVisit = '';
  let selectedLoadTimezone = '';
  // SPTL-484 follow-on: openUploadModal() defaulted this from the participant
  // but it was never declared → "selectedUploadTimezone is not defined"
  // ReferenceError on every upload-modal open. Declare it here.
  let selectedUploadTimezone = '';
  // Existing reading count for the sensor of the file being loaded
  let loadExistingCount = null;
  $: loadExistingCount = loadConfigFile ? (readingCountsByKey[loadConfigFile.Key] ?? null) : null;
  // Extract sensor/device id from the filename for display
  $: loadDeviceId = loadConfigFile ? extractDeviceIdFromFilename(loadConfigFile.Key) : '';
  
  // Pagination
  const ITEMS_PER_PAGE = 5;
  let currentPage = 1;
  $: totalPages = Math.ceil(files.length / ITEMS_PER_PAGE);
  $: paginatedFiles = files.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  function handlePageChange(page) {
    currentPage = page;
  }

  // Reset to first page when files change
  $: if (files.length > 0) {
    currentPage = 1;
  }

  // Computed values based on selection store
  $: hasStudy = $selectedStudy && $selectedStudy.protocolNumber;
  $: hasSite = $selectedSite && $selectedSite.siteId;
  $: hasParticipant = $selectedParticipant && $selectedParticipant.subjectId;

  $: entityType = (() => {
    if (hasStudy && !hasSite && !hasParticipant) return 'study';
    if (hasStudy && hasSite && !hasParticipant) return 'site';
    if (hasStudy && hasSite && hasParticipant) return 'participant';
    return 'study'; // Default to study if nothing is selected
  })();

  $: canLoadSensorData = $studyProperties?.features?.sensorUploads?.enabled === true;
  $: isSiteCgmExportEnabled = $studyProperties?.features?.siteCgmExport === true;

  $: entityId = (() => {
    return $selectedParticipant?.subjectId || $selectedSite?.siteId || $selectedStudy?.protocolNumber;
  })();

  $: protocolNumber = $selectedStudy?.protocolNumber;
  $: parentEntityId = $selectedStudy?.protocolNumber;
  $: parentSiteId = $selectedSite?.siteId;

  $: sensorUploadReaderType = $studyProperties?.features?.sensorUploads?.readerType || 'dexcom';
  $: sensorUploadTitle = sensorUploadReaderType === 'abbott'
    ? 'Load Abbott Sensor Data'
    : 'Load Dexcom Sensor Data';

  let showMenu = false;
  let menuFile = null;
  let sensorLogsByKey = {};
  let isLoadingLogs = false;
  let logsError = null;
  let readingCountsByKey = {};
  let isLoadingCounts = false;

  // Map of first (newest) IMPORTED file key per sensor id across the full sorted list
  $: firstImportedFileKeyBySensorId = (() => {
    const map = {};
    (files || []).forEach(f => {
      const name = (f?.Key || '').split('/').pop();
      const id = (name || '').split('_')[0] || '';
      const log = sensorLogsByKey?.[f.Key];
      if (id && log?.status === 'imported' && !map[id]) {
        map[id] = f.Key;
      }
    });
    return map;
  })();

  function handleMenuClick(file, event) {
    event.stopPropagation();
    menuFile = file;
    showMenu = !showMenu;
  }

  function handleMenuClose() {
    showMenu = false;
    menuFile = null;
  }

  async function handleCopyKey(key) {
    try {
      await navigator.clipboard.writeText(key);
      console.log('Copied S3 key:', key);
    } catch (e) {
      console.error('Failed to copy S3 key:', e);
      alert('Failed to copy S3 key');
    }
  }

  async function fetchSensorLogs() {
    if (entityType !== 'participant') return;
    if (selectedFileType !== FILE_TYPES.SENSOR.code) return;
    const keys = (files || []).map(f => f.Key).filter(Boolean);
    if (!keys.length) { sensorLogsByKey = {}; return; }
    try {
      isLoadingLogs = true;
      logsError = null;
      const params = new URLSearchParams();
      params.set('subjectGUID', $selectedParticipant?._id);
      const resp = await fetch(`/api/v1/sensorLogs?${params.toString()}`);
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || 'Failed to load sensor logs');
      // Two SensorLog rows can exist per file key: one written by the SPA at
      // upload (status='uploaded', inserted=0), one written by the backend
      // import job (status='imported', inserted=<count>). Pick the most
      // informative status so the row shows 'imported · N readings' instead of
      // the earlier 'uploaded · 0 readings'.
      const STATUS_RANK = { imported: 4, processing: 3, uploaded: 2, error: 1 };
      const byKey = {};
      for (const log of (data?.data || [])) {
        const existing = byKey[log.key];
        if (!existing || (STATUS_RANK[log.status] || 0) > (STATUS_RANK[existing.status] || 0)) {
          byKey[log.key] = log;
        }
      }
      sensorLogsByKey = byKey;
    } catch (e) {
      console.error('fetchSensorLogs error:', e);
      logsError = e.message;
    } finally {
      isLoadingLogs = false;
    }
  }

  function extractDeviceIdFromFilename(name) {
    if (!name) return '';
    const base = String(name).split('/').pop();
    if (!base.includes('_')) return '';
    return base.split('_')[0];
  }

  async function fetchFileReadingCount(file) {
    try {
      if (entityType !== 'participant') return;
      const filename = file?.Key || '';
      const externalId = extractDeviceIdFromFilename(filename);
      if (!externalId || !$selectedParticipant?._id) return;
      const params = new URLSearchParams();
      params.set('subjectGUID', $selectedParticipant._id);
      params.set('externalId', externalId);
      params.set('type', 'CGM');
      const url = `/api/v1/glucoseReadings/count?${params.toString()}`;
      const res = await fetch(url);
      const text = await res.text();
      let data;
      try { data = text ? JSON.parse(text) : {}; } catch (_) { data = {}; }
      const count = res.ok && typeof data?.count === 'number' ? data.count : 0;
      readingCountsByKey = { ...readingCountsByKey, [file.Key]: count };
    } catch (e) {
      console.error('fetchFileReadingCount error:', e);
      readingCountsByKey = { ...readingCountsByKey, [file.Key]: 0 };
    }
  }

  async function fetchReadingCounts() {
    if (entityType !== 'participant') return;
    const sensorFiles = (files || []).filter(f => (f.Key || '').split('/').slice(-2, -1)[0] === FILE_TYPES.SENSOR.code);
    if (sensorFiles.length === 0) { readingCountsByKey = {}; return; }
    try {
      isLoadingCounts = true;
      const tasks = sensorFiles.map(f => fetchFileReadingCount(f));
      await Promise.allSettled(tasks);
    } finally {
      isLoadingCounts = false;
    }
  }

  function openLoadConfigModal(file) {
    selectedLoadVisit = '';
    selectedLoadTimezone = $selectedParticipant?.timezoneCode || $selectedParticipant?.timezone || '';
    loadConfigFile = file;
    showLoadConfigModal = true;
    // Ensure we have an up-to-date reading count for this sensor
    fetchFileReadingCount(file);
    fetchTimezoneOptions();
  }

  function closeLoadConfigModal() {
    showLoadConfigModal = false;
    loadConfigFile = null;
  }

  async function handleDecryptImport(file) {
    try {
      showImportModal = true;
      importInProgress = true;
      importError = null;
      importSummary = { inserted: 0, attempted: 0, duplicates: 0, filename: file.Key.split('/').pop() };

      const fileType = file.Key.split('/').slice(-2, -1)[0];
      if (fileType !== FILE_TYPES.SENSOR.code) {
        importError = 'Decrypt/Import is only available for Sensor files.';
        importInProgress = false;
        return;
      }

      const body = {
        // QA-012: bucket resolved server-side.
        key: file.Key,
        subjectGUID: $selectedParticipant?._id, // backend expects subjectGUID (ObjectId string)
        timezone: $selectedParticipant?.timezone || 'UTC',
        protocolNumber: $selectedStudy?.protocolNumber
      };

      const resp = await fetch('/api/v1/glucoseReadings/import/abbott', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || 'Import failed');
      importSummary.inserted = data.inserted || 0;
      importSummary.attempted = data.attempted || data.inserted || 0;
      importSummary.duplicates = Math.max((importSummary.attempted - importSummary.inserted), 0);
      // Refresh logs after successful import
      await fetchSensorLogs();
    } catch (e) {
      console.error('Decrypt/Import error:', e);
      importError = e.message;
    }
    importInProgress = false;
  }

  async function handleRemoveSensorData(file) {
    try {
      const log = sensorLogsByKey?.[file.Key];
      if (!log?._id) {
        alert('No import record found for this file.');
        return;
      }
      if (!confirm('Remove sensor data imported from this file?')) return;
      const resp = await fetch(`/api/v1/sensorLogs/${log._id}`, { method: 'DELETE' });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || 'Failed to remove sensor data');
      await fetchSensorLogs();
    } catch (e) {
      console.error('handleRemoveSensorData error:', e);
      alert(e.message || 'Failed to remove sensor data');
    }
  }

  async function handleLoadSensorData(file) {
    try {
      showLoadProgressModal = true;
      loadProgressMessage = 'Loading sensor data…';
      const baseName = (file?.Key || '').split('/').pop() || '';
      const deviceId = baseName.split('_')[0] || '';
      const now = Date.now();
      const timezone = selectedLoadTimezone || $selectedParticipant?.timezoneCode || $selectedParticipant?.timezone || 'UTC';

      const deviceType = sensorUploadReaderType === 'abbott'
        ? 'Abbott FreeStyle Libre Pro'
        : 'Dexcom G7 Receiver';
      const manufacturer = sensorUploadReaderType === 'abbott' ? 'Abbott' : 'Dexcom';
      const model = sensorUploadReaderType === 'abbott'
        ? 'FreeStyle Libre (Receiver)'
        : 'G7 Receiver';
      const payload = {
        studyGUID: $selectedStudy?._id,
        siteGUID: $selectedSite?._id,
        subjectGUID: $selectedParticipant?._id,
        deviceType,
        fileName: file?.Key,
        timezone,
        uploadTimestamp: now,
        visitID: (selectedLoadVisit && (visitOptions.find(v => v.id === selectedLoadVisit)?.code)) || '',
        visitName: (selectedLoadVisit && (visitOptions.find(v => v.id === selectedLoadVisit)?.label)) || '',
        visitDate: now,
        visitDateOther: new Date(now).toISOString(),
        deviceId,
        md5Checksum: '',
        chunkNumber: 1,
        chunkSize: 1048576,
        currentChunkSize: file?.Size || 0,
        totalSize: file?.Size || 0,
        fielId: baseName,
        relativePath: baseName,
        totalChunks: 1,
        reason: 'Sensor Data Import',
        asyncFlag: false
      };
      console.log('payload: ', payload);

      loadRequestUrl = '/api/v1/cgm/import/data';
      loadRequestPayload = payload;
      const resp = await fetch(loadRequestUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loadRequestPayload)
      });
      let data = null;
      try { data = await resp.json(); } catch (_) { data = null; }
      if (typeof data?.__proxy?.asyncFlag === 'boolean') {
        loadRequestPayload = { ...loadRequestPayload, asyncFlag: data.__proxy.asyncFlag };
      }

      // Async mode: if API returns PENDING, poll status until UPLOADED or timeout
      let pollError = null;
      const POLL_INTERVAL_MS = 3000;
      const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
      if (resp.ok && data?.success === true && data?.item?.status === 'PENDING' && data?.item?.deviceEventLogGUID) {
        const guid = data.item.deviceEventLogGUID;
        const statusUrl = `/api/v1/cgm/import/data/status/${guid}`;
        const startedAt = Date.now();
        let statusData = data;
        while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
          loadProgressMessage = `Processing sensor data… (${Math.round((Date.now() - startedAt) / 1000)}s)`;
          const statusResp = await fetch(statusUrl);
          let statusJson = null;
          try { statusJson = await statusResp.json(); } catch (_) { statusJson = null; }
          if (!statusResp.ok || !statusJson?.success) {
            pollError = statusJson?.message || `Status check failed (HTTP ${statusResp.status})`;
            break;
          }
          statusData = statusJson;
          const itemStatus = statusJson?.item?.status;
          if (itemStatus === 'UPLOADED') {
            data = statusJson;
            break;
          }
          if (itemStatus && itemStatus !== 'PENDING') {
            pollError = `Unexpected status: ${itemStatus}`;
            data = statusJson;
            break;
          }
          await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
        }
        if (statusData?.item?.status !== 'UPLOADED') {
          pollError = pollError || 'Timed out waiting for import to complete';
          data = statusData;
        }
      }

      loadResultStatus = resp.status;
      loadResultOk = resp.ok && data?.success !== false && !pollError;
      loadResultData = data;
      loadResultError = !resp.ok
        ? (data?.message || `HTTP ${resp.status}`)
        : (data?.success === false ? (data?.message || 'Request returned success=false') : pollError);
      const insertedCount = (data && data.item && typeof data.item.readCount === 'number') ? data.item.readCount : (data && typeof data.count === 'number' ? data.count : null);
      loadResultSummary = loadResultOk
        ? `Loading of ${insertedCount != null ? insertedCount : 'the'} readings succeeded`
        : 'Loading failed';
      showLoadProgressModal = false;
      showLoadResultModal = true;
      showLoadRaw = false;
      // After a successful load, refresh counts for this file
      if (loadResultOk) {
        await fetchFileReadingCount(file);
      }

      // Create/update SensorLog record with audit via server endpoints
      try {
        const startRes = await fetch('/api/v1/sensorLogs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            // QA-012: bucket resolved server-side.
            key: file.Key,
            filename: baseName,
            manufacturer,
            model,
            externalId: deviceId,
            subjectGUID: $selectedParticipant?._id,
            protocolNumber: $selectedStudy?.protocolNumber,
            timezone,
            visitID: (selectedLoadVisit && (visitOptions.find(v => v.id === selectedLoadVisit)?.code)) || '',
            visitName: (selectedLoadVisit && (visitOptions.find(v => v.id === selectedLoadVisit)?.label)) || '',
            status: loadResultOk ? 'imported' : 'error',
            attempted: insertedCount || 0,
            inserted: insertedCount || 0,
            duplicates: 0,
            errorMessage: loadResultOk ? '' : (loadResultError || 'Failed to load sensor data'),
            requestPayload: loadRequestPayload,
            responsePayload: data
          })
        });
        if (!startRes.ok) {
          console.warn('Failed to write SensorLog');
        }
        // Refresh logs and counts after logging (ensures latest status/counts are shown)
        await fetchSensorLogs();
        await fetchReadingCounts();
      } catch (e) {
        console.warn('SensorLog write error:', e);
      }
    } catch (e) {
      console.error('handleLoadSensorData error:', e);
      loadResultStatus = null;
      loadResultOk = false;
      loadResultData = null;
      loadResultError = e.message || 'Failed to load sensor data';
      loadResultSummary = 'Loading failed';
      showLoadProgressModal = false;
      showLoadResultModal = true;
      showLoadRaw = false;
    }
  }

  async function handleLoadEnrollmentLog(file) {
    try {
      const filename = (file?.Key || '').split('/').pop() || '';
      const { isExcel } = isMediaFile(filename);
      if (!isExcel) {
        alert('Selected file is not an Excel file (.xlsx/.xls).');
        return;
      }

      // QA-012: fetch via backend proxy (no browser-direct S3).
      const downloadUrl = buildDownloadUrl(file);

      showLoadProgressModal = true;
      loadProgressMessage = 'Reading enrollment log…';

      const resp = await fetch(downloadUrl);
      if (!resp.ok) throw new Error('Failed to download file');
      const buf = await resp.arrayBuffer();

      // Read workbook and extract first worksheet
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const sheetName = wb.SheetNames[0];
      if (!sheetName) throw new Error('Excel file has no sheets');
      const ws = wb.Sheets[sheetName];

      // Validate required headers
      const headerRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      if (!headerRows.length) throw new Error('Excel sheet is empty');
      const headers = (headerRows[0] || []).map(h => String(h || '').trim());
      const required = ['STUDY','COUNTRY','SITE_id','PT','Gender','PT_STATUS','CPEVENT','VISIT_ACTUAL_DATE','CONTACT_TYPE_CODE'];
      const missing = required.filter(c => !headers.includes(c));
      if (missing.length) {
        throw new Error(`Missing required column(s): ${missing.join(', ')}`);
      }

      // Convert to JSON rows starting from header row
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const total = rows.length;
      if (!total) throw new Error('No data rows found');

      // Transform - use the selected study's protocolNumber instead of Excel STUDY column
      const currentProtocol = $selectedStudy?.protocolNumber || '';
      if (!currentProtocol) {
        throw new Error('No study selected. Please select a study before loading enrollment log.');
      }
      const mapped = rows.map(r => ({
        study: currentProtocol,
        country: String(r['COUNTRY'] ?? '').trim(),
        site: String(r['SITE_id'] ?? '').trim(),
        participant: String(r['PT'] ?? '').trim(),
        // Gender is intentionally ignored
        status: String(r['PT_STATUS'] ?? '').trim(),
        event: String(r['CPEVENT'] ?? '').trim(),
        // Store raw date string as provided in Excel
        date: String(r['VISIT_ACTUAL_DATE'] ?? '').trim(),
        info: String(r['CONTACT_TYPE_CODE'] ?? '').trim()
      })).filter(x => x.study && x.site && x.participant && x.event);

      // Chunk upload to backend with progress
      let processed = 0;
      let imported = 0;
      let skipped = 0;
      const chunkSize = 200;
      for (let i = 0; i < mapped.length; i += chunkSize) {
        const chunk = mapped.slice(i, i + chunkSize);
        loadProgressMessage = `Uploading enrollment log… ${Math.min(processed + chunk.length, total)} / ${total}`;
        const res = await fetch('/api/v1/enrolmentLog/import', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rows: chunk })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || 'Import failed');
        processed += data.processed || chunk.length;
        imported += data.inserted || 0;
        skipped += data.skipped || Math.max((chunk.length - (data.inserted || 0)), 0);
      }

      loadResultStatus = 200;
      loadResultOk = true;
      loadResultData = { processed, inserted: imported, skipped };
      loadResultError = null;
      loadResultSummary = `Enrollment log: processed ${processed}, imported ${imported}, skipped ${skipped}`;
      showLoadProgressModal = false;
      showLoadResultModal = true;
      showLoadRaw = false;
    } catch (e) {
      console.error('handleLoadEnrollmentLog error:', e);
      loadResultStatus = null;
      loadResultOk = false;
      loadResultData = null;
      loadResultError = e.message || 'Failed to load enrollment log';
      loadResultSummary = 'Loading failed';
      showLoadProgressModal = false;
      showLoadResultModal = true;
      showLoadRaw = false;
    }
  }

  // Add click handler to close menu when clicking outside
  function handleClickOutside(event) {
    if (showMenu && !event.target.closest('.menu-container')) {
      handleMenuClose();
    }
  }

  onMount(() => {
    // Give the stores time to initialize
    setTimeout(() => {
      isInitialized = true;
    }, 0);
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  // Debug logging for selection store values
  $: {
    console.log('Selection Store Debug:', {
      selectedStudy: {
        protocolNumber: $selectedStudy?.protocolNumber,
        isValid: hasStudy,
        full: $selectedStudy
      },
      selectedSite: {
        siteId: $selectedSite?.siteId,
        isValid: hasSite,
        full: $selectedSite
      },
      selectedParticipant: {
        subjectId: $selectedParticipant?.subjectId,
        isValid: hasParticipant,
        full: $selectedParticipant
      },
      computed: {
        entityType,
        entityId,
        protocolNumber,
        parentEntityId,
        parentSiteId
      }
    });
  }

  // QA-012: bucket name + credentials are server-side concerns. The SPA
  // calls /api/proxy/files/{list,download,upload,delete} and the backend
  // resolves the bucket from its own config. Always considered "configured"
  // from the browser's perspective; if the backend AWS is unset, the proxy
  // endpoints return a structured error which the catch blocks surface.
  const isAwsConfigured = true;

  /**
   * Backend-mediated replacement for browser-direct ListObjectsV2Command.
   * Returns the same shape the SPA used to derive from the AWS SDK
   * (an array of {Key, Size, LastModified, ETag}) so call sites are unchanged.
   */
  async function fetchListFiles(prefix, delimiter = '/') {
    const qs = new URLSearchParams({ prefix, delimiter });
    const response = await fetch(`/api/proxy/files/list?${qs.toString()}`);
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || `Failed to list files (${response.status})`);
    }
    return data.contents || [];
  }

  /**
   * Backend-mediated replacement for getSignedUrl(GetObjectCommand). Returns
   * the URL the browser can navigate to (or use as a video/img src). The
   * backend streams the bytes with Content-Disposition: attachment so a
   * click triggers download; for previews the same URL inlines correctly.
   */
  function buildDownloadUrl(file, extraParams = {}) {
    const ctx = getActionContext(file);
    const params = new URLSearchParams({
      key: file.Key,
      filename: file.Key.split('/').pop(),
      entityType: ctx.entityType || '',
      studyGUID: ctx.studyGUID || '',
      siteGUID: ctx.siteGUID || '',
      subjectGUID: ctx.subjectGUID || '',
      protocolNumber: ctx.protocolNumber || '',
      siteId: ctx.siteId || '',
      subjectId: ctx.subjectId || '',
      ...extraParams,
    });
    return `/api/proxy/files/download?${params.toString()}`;
  }

  // Add computed property for permissions
  $: canManageFiles = (
    $userRole === 'admin' ||
    $userRole === 'support' ||
    $userRole === 'study' ||
    (entityType === 'participant' && ($userRole === 'site' || $userRole === 'study'))
  );
  // Admin/support only actions (download, copy key, delete)
  $: canAdminFileActions = ($userRole === 'admin' || $userRole === 'support');
  $: canLoadFileActions = ($userRole === 'admin' || $userRole === 'support' || $userRole === 'study');
  // Download permission: same as canLoadFileActions, plus site users at the site level
  $: canDownloadFiles = (
    canLoadFileActions ||
    (entityType === 'site' && $userRole === 'site')
  );
  $: canManageSiteExports = (
    entityType === 'site' &&
    isSiteCgmExportEnabled &&
    ($userRole === 'admin' || $userRole === 'support' || $userRole === 'study' || $userRole === 'site') &&
    !isReadOnly()
  );
  $: canDownloadSiteExports = (
    entityType === 'site' &&
    isSiteCgmExportEnabled &&
    ($userRole === 'admin' || $userRole === 'support' || $userRole === 'study' || $userRole === 'site')
  );
  $: canDeleteSiteExports = entityType === 'site' && isSiteCgmExportEnabled && $userRole === 'admin';
  $: showStudyFilesOnSite = $studyProperties?.features?.documentManagement?.showStudyFilesOnSite === true;

  function getActionEntityType(file = null) {
    if (file?._isExport) return 'export';
    if (file?._isStudyFile) return 'study';
    return entityType;
  }

  function getActionContext(file = null) {
    return {
      entityType: getActionEntityType(file),
      studyGUID: $selectedStudy?._id || '',
      siteGUID: getActionEntityType(file) === 'study' ? '' : ($selectedSite?._id || ''),
      subjectGUID: $selectedParticipant?._id || '',
      protocolNumber: $selectedStudy?.protocolNumber || '',
      siteId: getActionEntityType(file) === 'study' ? '' : ($selectedSite?.siteId || ''),
      subjectId: $selectedParticipant?.subjectId || ''
    };
  }

  function shouldWriteSensorLog(fileType) {
    return entityType === 'participant' && fileType === FILE_TYPES.SENSOR.code && !!$selectedParticipant?._id;
  }

  async function triggerDownload(url) {
    // QA-017: post-QA-012 the download URL targets the backend proxy, which
    // requires JWT auth. Anchor-click navigation does NOT carry custom
    // headers, so the installAuthFetch Bearer-token interceptor is bypassed
    // and the request 401s. Fetch the bytes through window.fetch (wrapped by
    // installAuthFetch), then synthesize a Blob-URL anchor for the save.
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Download failed: HTTP ${resp.status}`);
    const blob = await resp.blob();

    let filename = '';
    const cd = resp.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/);
    if (m) {
      try { filename = decodeURIComponent(m[1]); } catch { filename = m[1]; }
    }
    if (!filename) {
      try {
        const qIdx = url.indexOf('?');
        const params = new URLSearchParams(qIdx >= 0 ? url.slice(qIdx + 1) : '');
        filename = params.get('filename') || 'download';
      } catch { filename = 'download'; }
    }

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  }

  function logDownloadAction(file, extra = {}) {
    try {
      const filename = file.Key.split('/').pop();
      fetch('/api/v1/file-actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: file._isExport ? 'export-download' : 'download',
          ...getActionContext(file),
          // QA-012: bucket resolved server-side.
          key: file.Key,
          filename,
          fileType: file._isExport ? 'exports' : file.Key.split('/').slice(-2, -1)[0],
          contentType: '',
          sizeBytes: file.Size || 0,
          source: 'files',
          metadata: { via: 'presigned-url' },
          ...extra
        })
      }).catch(() => {});
    } catch (_) {}
  }

  // Function to fetch visit options
  async function fetchVisitOptions() {
    try {
      const response = await fetch('/api/v1/lookups/VISIT');
      const data = await response.json();
      visitOptions = data.data || [];
    } catch (error) {
      console.error('Error fetching visit options:', error);
      visitOptions = [];
    }
  }

  async function fetchTimezoneOptions() {
    try {
      const res = await fetch(`/api/v1/lookups/TIMEZONE`);
      const data = await res.json();
      timezoneOptions = data?.data || [];
    } catch (e) {
      console.error('fetchTimezoneOptions error:', e);
      timezoneOptions = [];
    }
  }

  function getTimezoneOffset(timezone) {
    try {
      const date = new Date();
      const options = { timeZone: timezone, timeZoneName: 'short' };
      const formatter = new Intl.DateTimeFormat('en-US', options);
      const parts = formatter.formatToParts(date);
      const timeZonePart = parts.find((part) => part.type === 'timeZoneName');
      return timeZonePart ? ` (${timeZonePart.value})` : '';
    } catch (error) {
      console.error('Error getting timezone offset:', error);
      return '';
    }
  }

  // Organize timezones similar to Edit Participant
  function organizeTimezones(timezones) {
    const regions = {
      'United States': [],
      Canada: [],
      'America (Other)': [],
      Europe: [],
      Asia: [],
      Africa: [],
      Pacific: [],
      Australia: [],
      Atlantic: [],
      Indian: [],
      Other: []
    };

    timezones.forEach((tz) => {
      const [region] = String(tz.code || '').split('/');
      if (tz.code === 'Pacific/Honolulu') {
        regions['United States'].push(tz);
        return;
      }
      if (region === 'America') {
        if (
          tz.code.startsWith('America/New_York') ||
          tz.code.startsWith('America/Chicago') ||
          tz.code.startsWith('America/Denver') ||
          tz.code.startsWith('America/Los_Angeles') ||
          tz.code.startsWith('America/Phoenix') ||
          tz.code.startsWith('America/Anchorage') ||
          tz.code.startsWith('America/Adak') ||
          tz.code.startsWith('America/Boise') ||
          tz.code.startsWith('America/Detroit') ||
          tz.code.includes('Indiana') ||
          tz.code.includes('Kentucky') ||
          tz.code.includes('North_Dakota') ||
          tz.code.includes('Michigan') ||
          tz.code.startsWith('America/Juneau') ||
          tz.code.startsWith('America/Sitka') ||
          tz.code.startsWith('America/Metlakatla') ||
          tz.code.startsWith('America/Yakutat') ||
          tz.code.startsWith('America/Nome') ||
          tz.code.startsWith('America/Menominee')
        ) {
          regions['United States'].push(tz);
        } else if (
          tz.code.startsWith('America/Vancouver') ||
          tz.code.startsWith('America/Edmonton') ||
          tz.code.startsWith('America/Winnipeg') ||
          tz.code.startsWith('America/Toronto') ||
          tz.code.startsWith('America/Halifax') ||
          tz.code.startsWith('America/St_Johns')
        ) {
          regions['Canada'].push(tz);
        } else {
          regions['America (Other)'].push(tz);
        }
      } else if (regions[region]) {
        regions[region].push(tz);
      } else {
        regions['Other'].push(tz);
      }
    });

    // Sort within regions
    Object.keys(regions).forEach((region) => {
      regions[region].sort((a, b) => a.code.localeCompare(b.code));
    });

    return regions;
  }

  $: groupedTimezones = organizeTimezones(timezoneOptions);

  onMount(async () => {
    if (entityId) {
      await fetchFiles();
    }
    await fetchVisitOptions();
  });

  // Returns the study-level S3 prefix for a given file type (always {protocolNumber}/{fileType}/)
  function getStudyFilePrefix(fileType = selectedFileType) {
    if (!protocolNumber) return '';
    return `${protocolNumber}/${fileType}/`;
  }

  // Function to get the correct prefix based on entity type and IDs
  function getFilePrefix(fileType = selectedFileType) {
    if (!entityId || !protocolNumber) {
      console.error('Missing required IDs:', { entityType, entityId, protocolNumber });
      return '';
    }
    
    const basePath = `${protocolNumber}/`;
    
    switch (entityType) {
      case 'study':
        return `${basePath}${fileType}/`;
      case 'site':
        if (!parentEntityId) {
          console.error('Missing parentEntityId for site:', { entityType, entityId, parentEntityId });
          return '';
        }
        return `${basePath}${entityId}/${fileType}/`;
      case 'participant':
        if (!parentEntityId || !parentSiteId) {
          console.error('Missing parent IDs for participant:', { entityType, entityId, parentEntityId, parentSiteId });
          return '';
        }
        return `${basePath}${parentSiteId}/${entityId}/${fileType}/`;
      default:
        console.error('Invalid entityType:', { entityType, entityId, parentEntityId, parentSiteId });
        return '';
    }
  }

  // Auto-set file type filter for participants; ensure non-participant doesn't keep sensor selected
  $: if (entityType === 'participant') {
    selectedFileType = FILE_TYPES.SENSOR.code;
  } else if (selectedFileType === FILE_TYPES.SENSOR.code) {
    selectedFileType = FILE_TYPES.REPORTS.code;
  } else if (entityType !== 'site' && selectedFileType === FILE_TYPES.EXPORTS.code) {
    selectedFileType = FILE_TYPES.REPORTS.code;
  } else if (entityType === 'site' && selectedFileType === FILE_TYPES.EXPORTS.code && !isSiteCgmExportEnabled) {
    selectedFileType = FILE_TYPES.REPORTS.code;
  }

  // Watch for changes in selection store
  $: if (entityId && protocolNumber) {
    console.log('Selection changed:', { entityType, entityId, parentEntityId, parentSiteId, protocolNumber });
    fetchFiles();
  }

  // Fetch study-level files for given file types and tag them with _isStudyFile
  async function fetchStudyFiles(fileTypes) {
    const studyFiles = [];
    for (const type of fileTypes) {
      const prefix = getStudyFilePrefix(type.code);
      if (!prefix) continue;
      const contents = await fetchListFiles(prefix);
      const typeFiles = contents.filter(file => {
        const relativePath = file.Key.slice(prefix.length);
        return !relativePath.includes('/');
      }).map(file => ({ ...file, _isStudyFile: true }));
      studyFiles.push(...typeFiles);
    }
    return studyFiles;
  }

  async function fetchSiteExports() {
    const query = new URLSearchParams({
      siteGUID: $selectedSite?._id || '',
      studyGUID: $selectedStudy?._id || ''
    });
    const response = await fetch(`/api/v1/site-cgm-exports?${query.toString()}`);
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || 'Failed to load site exports');
    }

    return (data.data || []).map((job) => ({
      Key: job.zipS3Key || `${protocolNumber}/${$selectedSite?.siteId || 'site'}/${FILE_TYPES.EXPORTS.code}/${job.zipFilename}`,
      Size: job.zipSizeBytes || 0,
      LastModified: job.completedAt || job.createdAt,
      _isExport: true,
      _exportMeta: job
    }));
  }

  async function fetchFiles() {
    if (!entityId || !protocolNumber) {
      console.log('Skipping file fetch - missing required information:', { entityId, protocolNumber });
      return;
    }
    
    isLoading = true;
    error = null;
    
    if (!isAwsConfigured) {
      error = 'AWS configuration is missing. Please check your environment variables.';
      isLoading = false;
      return;
    }
    
    try {
      if (entityType === 'site' && selectedFileType === FILE_TYPES.EXPORTS.code) {
        files = await fetchSiteExports();
        return;
      }

      if (selectedFileType === FILE_TYPES.ALL.code) {
        // For ALL, we need to fetch files from all file types
        const fileTypes = Object.values(FILE_TYPES).filter(type => (
          type.code !== FILE_TYPES.ALL.code &&
          type.code !== FILE_TYPES.EXPORTS.code &&
          (entityType === 'participant' ? true : type.code !== FILE_TYPES.SENSOR.code)
        ));
        const allFiles = [];
        
        for (const type of fileTypes) {
          const prefix = getFilePrefix(type.code);
          if (!prefix) {
            error = 'Invalid entity configuration';
            return;
          }

          const contents = await fetchListFiles(prefix);
          const typeFiles = contents.filter(file => {
            const relativePath = file.Key.slice(prefix.length);
            return !relativePath.includes('/');
          });
          allFiles.push(...typeFiles);
        }

        // When at site level, also include study-level files
        if (entityType === 'site' && showStudyFilesOnSite) {
          const studyFiles = await fetchStudyFiles(fileTypes);
          allFiles.push(...studyFiles);
        }
        
        // Sort newest first
        allFiles.sort((a, b) => (new Date(b.LastModified)) - (new Date(a.LastModified)));
        files = allFiles;
        await fetchSensorLogs();
        await fetchReadingCounts();
      } else {
        const prefix = getFilePrefix();
        if (!prefix) {
          error = 'Invalid entity configuration';
          return;
        }

        const contents = await fetchListFiles(prefix);
        const siteFiles = contents.filter(file => {
          const relativePath = file.Key.slice(prefix.length);
          return !relativePath.includes('/');
        });

        // When at site level, also include study-level files for the current type
        let combined = [...siteFiles];
        if (entityType === 'site' && showStudyFilesOnSite) {
          const fileTypes = [{ code: selectedFileType }];
          const studyFiles = await fetchStudyFiles(fileTypes);
          combined.push(...studyFiles);
        }

        // Sort newest first
        combined.sort((a, b) => (new Date(b.LastModified)) - (new Date(a.LastModified)));
        files = combined;
        await fetchSensorLogs();
        await fetchReadingCounts();
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      error = 'Failed to load files: ' + (err.message || 'Unknown error');
    } finally {
      isLoading = false;
    }
  }


  async function handleDelete(file) {
    if (!confirm('Are you sure you want to delete this file?')) return;

    if (!isAwsConfigured) {
      error = 'AWS configuration is missing. Please check your environment variables.';
      return;
    }

    isLoading = true;
    error = null;

    try {
      const response = await fetch('/api/proxy/files/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...getActionContext(file),
          key: file.Key,
          filename: file.Key.split('/').pop(),
          fileType: file.Key.split('/').slice(-2, -1)[0],
          contentType: '',
          sizeBytes: file.Size || 0
        })
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'Failed to delete file');
      }

      const fileType = file.Key.split('/').slice(-2, -1)[0];
      if (shouldWriteSensorLog(fileType)) {
        try {
          await fetch('/api/v1/sensorLogs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              // QA-012: bucket resolved server-side; omit from request body.
              key: file.Key,
              filename: file.Key.split('/').pop(),
              manufacturer: 'N/A',
              model: 'N/A',
              externalId: '',
              subjectGUID: $selectedParticipant?._id,
              protocolNumber: $selectedStudy?.protocolNumber,
              timezone: $selectedParticipant?.timezoneCode || $selectedParticipant?.timezone || 'UTC',
              status: 'removed',
              attempted: 0,
              inserted: 0,
              duplicates: 0,
              errorMessage: '',
              requestPayload: { action: 'delete' },
              responsePayload: { ok: true }
            })
          });
        } catch (e) {
          console.warn('SensorLog delete write error:', e);
        }
      }
      await fetchFiles();
    } catch (err) {
      console.error('Error deleting file:', err);
      error = 'Failed to delete file: ' + (err.message || 'Unknown error');
    } finally {
      isLoading = false;
    }
  }

  async function handleDownload(file) {
    if (!isAwsConfigured) {
      error = 'AWS configuration is missing. Please check your environment variables.';
      return;
    }

    try {
      // QA-012: trigger backend-proxied download (returns attachment).
      logDownloadAction(file);
      await triggerDownload(buildDownloadUrl(file));
    } catch (err) {
      console.error('Error generating download URL:', err);
      error = 'Failed to download file: ' + (err.message || 'Unknown error');
    }
  }

  async function handleSiteExportDownload(file) {
    if (!isAwsConfigured) {
      error = 'AWS configuration is missing. Please check your environment variables.';
      return;
    }

    const exportId = file?._exportMeta?.id;
    if (!exportId) {
      error = 'Export file is missing its identifier';
      return;
    }

    try {
      // QA-012: trigger backend-proxied download.
      logDownloadAction(file, { exportJobGUID: exportId });
      await triggerDownload(buildDownloadUrl(file));
    } catch (err) {
      console.error('Error generating download URL:', err);
      error = 'Failed to download file: ' + (err.message || 'Unknown error');
    }

    setTimeout(() => {
      if (entityType === 'site' && selectedFileType === FILE_TYPES.EXPORTS.code) {
        fetchFiles();
      }
    }, 1200);
  }

  async function handleDeleteSiteExport(file) {
    const exportId = file?._exportMeta?.id;
    if (!exportId) {
      error = 'Export file is missing its identifier';
      return;
    }

    if (!confirm('Are you sure you want to delete this export file?')) return;

    isLoading = true;
    error = null;

    try {
      const response = await fetch(`/api/v1/site-cgm-exports?jobId=${encodeURIComponent(exportId)}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'Failed to delete export file');
      }
      await fetchFiles();
    } catch (err) {
      console.error('Error deleting export file:', err);
      error = err.message || 'Failed to delete export file';
    } finally {
      isLoading = false;
    }
  }

  function handleClose() {
    dispatch('close');
    onClose();
  }

  function formatFileSize(bytes) {
    if (bytes >= 1024 * 1024) {
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    }
    return (bytes / 1024).toFixed(2) + ' KB';
  }

  function isMediaFile(filename) {
    const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'));
    return {
      isImage: IMAGE_EXTENSIONS.includes(ext),
      isVideo: VIDEO_EXTENSIONS.includes(ext),
      isPdf: PDF_EXTENSIONS.includes(ext),
      isExcel: OFFICE_EXTENSIONS.EXCEL.includes(ext),
      isPowerPoint: OFFICE_EXTENSIONS.POWERPOINT.includes(ext)
    };
  }

  async function handlePreview(key) {
    if (!isAwsConfigured) {
      error = 'AWS configuration is missing. Please check your environment variables.';
      return;
    }

    try {
      // QA-012: preview URL also flows through the backend proxy. The Office
      // Online Viewer needs an externally-accessible URL — keep its existing
      // wrapper but point at the backend route. Note: Office Online needs
      // public read; once that pain point matters, we'll switch to a presigned
      // URL minted by the backend (a separate /presign endpoint) instead.
      const previewKeyUrl = buildDownloadUrl({ Key: key });
      const fullUrl = new URL(previewKeyUrl, window.location.origin).toString();
      const filename = key.split('/').pop();
      const { isImage, isVideo, isPdf, isExcel, isPowerPoint } = isMediaFile(filename);

      if (isImage || isVideo || isPdf || isExcel || isPowerPoint) {
        if (isExcel || isPowerPoint) {
          // Use Microsoft Office Online Viewer for Excel and PowerPoint files
          previewUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fullUrl)}`;
          previewType = isExcel ? 'excel' : 'powerpoint';
        } else {
          // QA-017: <img>/<video>/<iframe> src= triggers browser navigation,
          // which bypasses installAuthFetch — no Bearer header → 401. Fetch
          // through window.fetch (wrapped) and bind a Blob URL into the src.
          const resp = await fetch(previewKeyUrl);
          if (!resp.ok) throw new Error(`Preview failed: HTTP ${resp.status}`);
          const blob = await resp.blob();
          if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
          previewBlobUrl = URL.createObjectURL(blob);
          previewUrl = previewBlobUrl;
          previewType = isImage ? 'image' : isVideo ? 'video' : 'pdf';
        }
        showPreview = true;
      } else {
        window.open(signedUrl, '_blank');
      }
    } catch (err) {
      console.error('Error generating preview URL:', err);
      error = 'Failed to preview file: ' + (err.message || 'Unknown error');
    }
  }

  function closePreview() {
    showPreview = false;
    previewUrl = '';
    previewType = '';
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      previewBlobUrl = '';
    }
  }

  // Upload modal functions
  function openUploadModal() {
    showUploadModal = true;
    selectedFile = null;
    // Auto-select sensor type for participants
    selectedUploadType = entityType === 'participant' ? FILE_TYPES.SENSOR.code : '';
    selectedVisit = '';
    selectedUploadTimezone = $selectedParticipant?.timezoneCode || $selectedParticipant?.timezone || '';
    // Reset overwrite warning state
    showOverwriteWarning = false;
    conflictingFile = null;
    suggestedFilename = '';
    fetchTimezoneOptions();
  }

  function closeUploadModal() {
    showUploadModal = false;
    selectedFile = null;
    selectedUploadType = '';
    selectedVisit = '';
    // Reset overwrite warning state
    showOverwriteWarning = false;
    conflictingFile = null;
    suggestedFilename = '';
    if (fileInput) {
      fileInput.value = '';
    }
  }

  function handleFileSelect(event) {
    const file = event.target.files[0];
    if (file) {
      selectedFile = file;
    }
  }

  function handleDragOver(event) {
    event.preventDefault();
    isDragOver = true;
  }

  function handleDragLeave(event) {
    event.preventDefault();
    isDragOver = false;
  }

  function handleDrop(event) {
    event.preventDefault();
    isDragOver = false;
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      selectedFile = files[0];
    }
  }

  // Reactive filename generation
  $: generatedFilename = (() => {
    if (!selectedFile) return '';
    
    const originalName = selectedFile.name;
    const extension = originalName.substring(originalName.lastIndexOf('.'));
    const baseName = originalName.substring(0, originalName.lastIndexOf('.'));
    
    let newName = baseName;
    
    if (augmentFilenames) {
      // Add protocol number if available
      if (protocolNumber) {
        newName += `_${protocolNumber}`;
      }
      
      // Add site ID if available (for site and participant files)
      if (parentSiteId) {
        newName += `_${parentSiteId}`;
      }
      
      // Add subject ID if available (for participant files)
      if (entityType === 'participant' && entityId) {
        newName += `_${entityId}`;
      }
      
      // Add visit code if selected
      if (selectedVisit) {
        const visitInfo = visitOptions.find(v => v.id === selectedVisit);
        if (visitInfo) {
          newName += `_${visitInfo.code}`;
        }
      }
    }
    
    return newName + extension;
  })();

  // Function to check if a file already exists (QA-012: via backend /list)
  async function checkFileExists(key) {
    try {
      const contents = await fetchListFiles(key, '');
      return Array.isArray(contents) && contents.some(obj => obj.Key === key);
    } catch (err) {
      console.error('checkFileExists error:', err);
      throw err;
    }
  }

  // Function to generate a unique filename with suffix
  function generateUniqueFilename(generatedFilename) {
    // Extract the base name and extension from the generated filename
    const lastDotIndex = generatedFilename.lastIndexOf('.');
    const baseName = generatedFilename.substring(0, lastDotIndex);
    const extension = generatedFilename.substring(lastDotIndex);
    
    let counter = 1;
    let newFilename = generatedFilename;
    
    // Check if any existing file has this name pattern
    const existingNames = files.map(f => f.Key.split('/').pop());
    
    while (existingNames.includes(newFilename)) {
      newFilename = `${baseName}_${counter}${extension}`;
      counter++;
    }
    
    return newFilename;
  }

  // Function to handle overwrite confirmation
  function handleOverwriteConfirm() {
    showOverwriteWarning = false;
    conflictingFile = null;
    suggestedFilename = '';
    // Proceed with upload using the original filename
    performUpload(generatedFilename);
  }

  // Function to handle auto-suffix option
  function handleAutoSuffix() {
    // Generate unique filename based on the generated filename
    const uniqueFilename = generateUniqueFilename(generatedFilename);
    showOverwriteWarning = false;
    conflictingFile = null;
    suggestedFilename = '';
    // Proceed with upload using the unique filename
    performUpload(uniqueFilename);
  }

  // Function to cancel overwrite and go back to upload modal
  function handleOverwriteCancel() {
    showOverwriteWarning = false;
    conflictingFile = null;
    suggestedFilename = '';
  }

  // Main upload function
  async function performUpload(filename) {
    if (!entityId || !protocolNumber) {
      error = 'Missing required entity information';
      return;
    }

    if (!isAwsConfigured) {
      error = 'AWS configuration is missing. Please check your environment variables.';
      return;
    }

    isLoading = true;
    error = null;
    uploadProgress = 0;

    try {
      // Use the selected upload type for the file prefix
      const prefix = getFilePrefix(selectedUploadType);
      if (!prefix) {
        error = 'Invalid entity configuration';
        return;
      }

      const key = `${prefix}${filename}`;
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('key', key);
      formData.append('filename', filename);
      formData.append('fileType', selectedUploadType);
      formData.append('entityType', entityType);
      formData.append('studyGUID', $selectedStudy?._id || '');
      formData.append('siteGUID', $selectedSite?._id || '');
      formData.append('subjectGUID', $selectedParticipant?._id || '');
      formData.append('protocolNumber', $selectedStudy?.protocolNumber || '');
      formData.append('siteId', $selectedSite?.siteId || '');
      formData.append('subjectId', $selectedParticipant?.subjectId || '');

      const response = await fetch('/api/proxy/files/upload', {
        method: 'POST',
        body: formData
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'Failed to upload file');
      }

      if (shouldWriteSensorLog(selectedUploadType)) {
        try {
          await fetch('/api/v1/sensorLogs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              // QA-012: bucket resolved server-side; omit from request body.
              key,
              filename,
              manufacturer: 'N/A',
              model: 'N/A',
              externalId: '',
              subjectGUID: $selectedParticipant?._id || null,
              protocolNumber: $selectedStudy?.protocolNumber || null,
              timezone: $selectedParticipant?.timezoneCode || $selectedParticipant?.timezone || 'UTC',
              status: 'uploaded',
              attempted: 0,
              inserted: 0,
              duplicates: 0,
              errorMessage: '',
              requestPayload: { action: 'upload', size: selectedFile.size, type: selectedFile.type },
              responsePayload: { ok: true }
            })
          });
        } catch (e) {
          console.warn('SensorLog upload write error:', e);
        }
      }
      await fetchFiles();
      closeUploadModal();
    } catch (err) {
      console.error('Error uploading file:', err);
      error = 'Failed to upload file: ' + (err.message || 'Unknown error');
    } finally {
      isLoading = false;
      uploadProgress = 0;
    }
  }

  async function handleModalUpload() {
    if (!selectedFile || !selectedUploadType) {
      error = 'Please select a file and file type';
      return;
    }

    // Use the reactive generated filename
    const newFilename = generatedFilename;
    
    // Use the selected upload type for the file prefix
    const prefix = getFilePrefix(selectedUploadType);
    if (!prefix) {
      error = 'Invalid entity configuration';
      return;
    }

    const key = `${prefix}${newFilename}`;
    
    // Check if file already exists
    try {
      const fileExists = await checkFileExists(key);
      if (fileExists) {
        // For sensor files, do not allow overwrite or rename
        conflictingFile = { Key: key, filename: newFilename };
        showOverwriteWarning = true;
        return;
      }
    } catch (err) {
      console.error('Error checking file existence:', err);
      error = 'Failed to check if file exists: ' + (err.message || 'Unknown error');
      return;
    }

    // File doesn't exist, proceed with upload
    await performUpload(newFilename);
  }
</script>

<div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow">
  <div class="flex justify-between items-center mb-2 pl-4 pr-2 pt-1">
    <h3 class="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
    <div class="flex items-center gap-0 ml-auto">
      <div class="flex items-baseline gap-2">
        <span class="text-sm text-gray-800 dark:text-gray-100">Type:</span>
        <select
          bind:value={selectedFileType}
          on:change={() => fetchFiles()}
          disabled={entityType === 'participant'}
          class="form-select h-6 w-28 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none {entityType === 'participant' ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}"
        >
          {#each Object.values(FILE_TYPES).filter(type => {
            if (entityType === 'participant') {
              return type.code === FILE_TYPES.SENSOR.code;
            }
            if (type.code === FILE_TYPES.SENSOR.code) return false;
            if (type.code === FILE_TYPES.EXPORTS.code) return entityType === 'site' && isSiteCgmExportEnabled;
            return true;
          }) as type}
            <option value={type.code}>{type.label}</option>
          {/each}
        </select>
      </div>
      {#if entityType === 'site' && selectedFileType === FILE_TYPES.EXPORTS.code}
        <button
          class="icon-btn ml-2"
          title={!canManageSiteExports ? 'Insufficient permissions' : 'Start site CGM export'}
          disabled={!canManageSiteExports}
          class:opacity-50={!canManageSiteExports}
          class:cursor-not-allowed={!canManageSiteExports}
          on:click={() => (showSiteExportModal = true)}
        >
          <FontAwesomeIcon icon={faCloudArrowUp} />
        </button>
      {:else}
        <button
          class="icon-btn ml-2"
          title={!canManageFiles ? 'Insufficient permissions' : 'Upload file'}
          disabled={!isAwsConfigured || !canManageFiles}
          class:opacity-50={!isAwsConfigured || !canManageFiles}
          class:cursor-not-allowed={!isAwsConfigured || !canManageFiles}
          on:click={openUploadModal}
        >
          <FontAwesomeIcon icon={faCloudArrowUp} />
        </button>
      {/if}
      <button
        class="icon-btn"
        on:click={fetchFiles}
        disabled={isLoading || !isAwsConfigured}
        class:opacity-50={!isAwsConfigured}
        class:cursor-not-allowed={!isAwsConfigured}
        title="Refresh files"
      >
        <FontAwesomeIcon icon={faRotate} />
      </button>
      <button
        class="icon-btn"
        on:click={handleClose}
        title="Close files view"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  </div>

  {#if !protocolNumber}
    <div class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4 mx-4">
      Protocol number is required for file operations.
    </div>
  {/if}

  <SiteCgmExportModal
    show={showSiteExportModal}
    studyGUID={$selectedStudy?._id || ''}
    siteGUID={$selectedSite?._id || ''}
    protocolNumber={$selectedStudy?.protocolNumber || ''}
    siteId={$selectedSite?.siteId || ''}
    on:close={() => (showSiteExportModal = false)}
    on:completed={() => fetchFiles()}
  />

  {#if showLoadConfigModal}
    <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={closeLoadConfigModal}></div>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div class="relative bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg max-w-md w-full mx-4 z-60" on:click|stopPropagation>
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{sensorUploadTitle}</h3>
          <button
            class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            on:click={closeLoadConfigModal}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div class="space-y-4">
          {#if loadExistingCount != null && loadExistingCount > 0}
            <div class="border border-yellow-300 text-gray-900 dark:text-white px-3 py-2 text-sm rounded">
              Sensor {loadDeviceId} already has {loadExistingCount} reading{loadExistingCount === 1 ? '' : 's'} loaded. Loading will remove existing CGM readings and reload all readings from the uploaded file.
            </div>
          {/if}
          <div>
            <label for="loadTimezoneSelect" class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Timezone
            </label>
            <select
              id="loadTimezoneSelect"
              bind:value={selectedLoadTimezone}
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            >
              <option value="">Select timezone</option>
              {#each Object.entries(groupedTimezones) as [region, tzs]}
                <optgroup label={region}>
                  {#each tzs as tz}
                    <option value={tz.code}>{tz.code}{getTimezoneOffset(tz.code)}</option>
                  {/each}
                </optgroup>
              {/each}
            </select>
          </div>

          {#if isFeatureEnabled('visitTags', $studyProperties)}
            <div>
              <label for="loadVisitSelect" class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Visit (Optional)
              </label>
              <select
                id="loadVisitSelect"
                bind:value={selectedLoadVisit}
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="">Select visit (optional)</option>
                {#each visitOptions as visit}
                  <option value={visit.id}>{visit.label}</option>
                {/each}
              </select>
            </div>
          {/if}

          <div class="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              on:click={closeLoadConfigModal}
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              on:click={() => { if (loadConfigFile) handleLoadSensorData(loadConfigFile); closeLoadConfigModal(); }}
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Load
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}

  <!-- QA-012: removed the "AWS configuration is missing" SPA error block.
       All S3 ops now go through the backend FileProxyController so the SPA
       no longer needs VITE_AWS_* credentials. -->


  <!-- Import Modal -->
  {#if showImportModal}
    <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <button type="button" class="absolute inset-0 bg-slate-800 opacity-75" aria-label="Close"
        on:click={() => { if (!importInProgress) showImportModal = false; }}></button>
      <div class="relative bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg max-w-md w-full mx-4 z-60" role="dialog" aria-modal="true">
        <div class="flex justify-between items-center mb-3">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Decrypt & Import</h3>
          <button
            class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            on:click={() => { if (!importInProgress) showImportModal = false; }}
            disabled={importInProgress}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {#if importInProgress}
          <p class="text-sm text-gray-700 dark:text-gray-300 mb-3">Processing {importSummary.filename}...</p>
          <div class="flex items-center gap-3">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <span class="text-sm text-gray-600 dark:text-gray-400">Decrypting and importing readings...</span>
          </div>
        {:else}
          {#if importError}
            <div class="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-3">
              {importError}
            </div>
          {:else}
            <div class="bg-gray-50 dark:bg-gray-800 p-3 rounded-md mb-3">
              <div class="text-sm text-gray-700 dark:text-gray-300">Filename: <span class="font-mono">{importSummary.filename}</span></div>
              <div class="text-sm text-gray-700 dark:text-gray-300">Inserted: <span class="font-semibold">{importSummary.inserted}</span></div>
              <div class="text-sm text-gray-700 dark:text-gray-300">Duplicates: <span class="font-semibold">{importSummary.duplicates}</span></div>
              {#if importSummary.attempted}
                <div class="text-xs text-gray-500 dark:text-gray-400">Attempted: {importSummary.attempted}</div>
              {/if}
            </div>
          {/if}

          <div class="flex justify-end">
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              on:click={() => { showImportModal = false; importError = null; }}
            >
              Ok
            </button>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if error}
    <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mx-4">
      {error}
    </div>
  {/if}

  {#if showPreview}
    <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={closePreview}></div>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div class="relative bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg max-w-[90vw] max-h-[90vh] z-60" on:click|stopPropagation>
        <button
          class="absolute top-2 right-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          on:click={closePreview}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        {#if previewType === 'image'}
          <img src={previewUrl} alt="Preview" class="max-w-full max-h-[85vh] object-contain" />
        {:else if previewType === 'video'}
          <!-- svelte-ignore a11y-media-has-caption -->
          <video src={previewUrl} controls class="max-w-full max-h-[85vh]">
            Your browser does not support the video tag.
          </video>
        {:else if previewType === 'pdf'}
          <div class="flex justify-center w-full">
            <iframe
              src={previewUrl}
              class="w-[800px] h-[85vh] border-0"
              title="PDF Preview"
            ></iframe>
          </div>
        {:else if previewType === 'excel' || previewType === 'powerpoint'}
          <div class="flex justify-center w-full">
            <iframe
              src={previewUrl}
              class="w-[90vw] h-[85vh] border-0"
              title={`${previewType === 'excel' ? 'Excel' : 'PowerPoint'} Preview`}
              frameborder="0"
              allowfullscreen="true"
            ></iframe>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if isLoading}
    <div class="flex justify-center items-center py-4">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>
  {:else if files.length === 0}
    <div class="text-center pb-4 text-gray-500 dark:text-gray-400">
      no documents available
    </div>
  {:else}
    <div class="px-0 pt-0">
      <table class="min-w-full border-collapse table-auto">
        <thead>
          <tr class="text-sm text-black dark:text-slate-300">
            <th class="px-4 py-2 text-left bold">Type</th>
            <th class="px-4 py-2 text-left bold">Name</th>
            <th class="px-4 py-2 text-left bold">Size</th>
            <th class="px-4 py-2 text-left bold">File Date</th>
            <th class="w-8 px-4 py-2 text-left bold">&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {#each paginatedFiles as file, index}
            {@const fileType = file._isExport ? FILE_TYPES.EXPORTS.code : file.Key.split('/').slice(-2, -1)[0]}
            {@const fileTypeInfo = Object.values(FILE_TYPES).find(t => t.code === fileType) || FILE_TYPES.OTHER}
            {@const filename = file.Key.split('/').pop()}
            {@const deviceIdForCount = filename.split('_')[0]}
            {@const { isImage, isVideo, isPdf, isExcel, isPowerPoint } = isMediaFile(filename)}
            {@const exportMeta = file._exportMeta}
            <tr class="text-sm text-gray-800 dark:text-slate-300 {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'}">
              <td class="px-4 py-2">
                {fileTypeInfo.label}
                {#if file._isStudyFile}
                  <span class="ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-700" title="Shared from study level">Study</span>
                {/if}
                {#if file._isExport && exportMeta?.status}
                  <span class="ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium border border-gray-300 dark:border-gray-600">
                    {exportMeta.status}
                  </span>
                {/if}
              </td>
              <td class="px-4 py-2 break-words">
                {filename}
                {#if file._isExport}
                  <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Requested by {exportMeta?.requestedByName || exportMeta?.requestedByEmail || 'Unknown'} on {new Date(exportMeta?.createdAt || file.LastModified).toLocaleString()}
                  </div>
                  {#if exportMeta?.downloadLog?.length}
                    <div
                      class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                      title={exportMeta.downloadLog.map((log) => `${log.actorName || log.actorEmail || 'Unknown'} - ${new Date(log.createdAt).toLocaleString()}`).join('\n')}
                    >
                      Last download: {exportMeta.downloadLog[0].actorName || exportMeta.downloadLog[0].actorEmail || 'Unknown'} on {new Date(exportMeta.downloadLog[0].createdAt).toLocaleString()}
                    </div>
                  {/if}
                {/if}
                {#if entityType === 'participant' && fileType === FILE_TYPES.SENSOR.code}
                  <!-- Sensor ID tag -->
                  <span class="ml-2 inline-flex items-center rounded px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-500" title="Sensor ID">
                    {deviceIdForCount}
                  </span>
                  {@const logForCount = sensorLogsByKey[file.Key]}
                  {@const currentCount = readingCountsByKey[file.Key]}
                  <span
                    class="ml-2 inline-flex items-center rounded px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-500"
                    title={isLoadingCounts && currentCount == null ? 'calculating…' : `Current: ${(currentCount || 0)} readings`}
                  >
                    {#if logForCount}
                      {(logForCount.inserted ?? 0)} readings
                    {:else if isLoadingCounts && currentCount == null}
                      calculating…
                    {:else}
                      {(currentCount || 0)} readings
                    {/if}
                  </span>
                  {#if isFeatureEnabled('visitTags', $studyProperties) && sensorLogsByKey[file.Key]?.visitName}
                    <span class="ml-1 inline-flex items-center rounded px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-500" title="Visit">
                      {sensorLogsByKey[file.Key].visitName}
                    </span>
                  {/if}
                {/if}
                {#if entityType === 'participant' && fileType === FILE_TYPES.SENSOR.code}
                  {#if sensorLogsByKey[file.Key]}
                    {@const log = sensorLogsByKey[file.Key]}
                    {@const isNewestImported = firstImportedFileKeyBySensorId[deviceIdForCount] === file.Key}
                    {@const displayStatus = (log.status === 'imported' && !isNewestImported) ? 'archived' : log.status}
                    <span class="ml-2 inline-flex items-center rounded px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-500"
                      title={`${displayStatus === 'imported' 
                        ? `Attempted: ${log.attempted ?? 0}\nInserted: ${log.inserted ?? 0}\nDuplicates: ${((log.duplicates ?? (Math.max((log.attempted ?? 0) - (log.inserted ?? 0), 0))))}`
                        : (displayStatus === 'error' && log.errorMessage ? `${log.errorMessage}` : '')}`}
                    >
                      <span class="mr-1 inline-block h-2 w-2 rounded-full"
                        class:bg-green-500={displayStatus === 'imported'}
                        class:bg-yellow-500={displayStatus === 'processing'}
                        class:bg-red-500={displayStatus === 'error'}
                        class:bg-gray-400={displayStatus === 'removed'}
                        class:bg-blue-500={displayStatus === 'uploaded'}
                        class:bg-gray-500={displayStatus === 'archived'}
                      ></span>
                      {displayStatus}
                    </span>
                  {:else}
                    {#if isLoadingLogs}
                      <span class="ml-2 text-xs text-gray-500">checking…</span>
                    {/if}
                  {/if}
                {/if}
                {#if isImage || isVideo || isPdf || isExcel || isPowerPoint}
                  <button
                    class="pagination mx-1 p-0"
                    on:click={() => handlePreview(file.Key)}
                    disabled={!isAwsConfigured}
                    class:opacity-50={!isAwsConfigured}
                    class:cursor-not-allowed={!isAwsConfigured}
                    title="Preview file"
                  >
                    <FontAwesomeIcon icon={faEye} class="text-sky-800 dark:text-white/70" />
                  </button>
                {/if}
              </td>
              <td class="px-4 py-2">{formatFileSize(file.Size)}</td>
              <td class="px-4 py-2">{new Date(file.LastModified).toLocaleString()}</td>
              <td class="px-4 py-2">
                <div class="menu-container relative">
                  <button
                    on:click={(e) => handleMenuClick(file, e)}
                    class="pagination mx-1 p-0"
                    title="More options"
                  >
                    <FontAwesomeIcon icon={faEllipsis} class="text-sky-800 dark:text-white/70" />
                  </button>
                  {#if showMenu && menuFile?.Key === file.Key}
                    <div class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50">
                      {#if enableLegacySensorActions}
                        <button
                          on:click={() => {
                            handleDecryptImport(file);
                            handleMenuClose();
                          }}
                          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                          disabled={!isAwsConfigured || !canManageFiles}
                          class:opacity-50={!isAwsConfigured || !canManageFiles}
                          title={!isAwsConfigured ? 'AWS configuration missing' : 'Import Abbott sensor data'}
                        >
                          <span>Import Sensor Data</span>
                          <FontAwesomeIcon icon={faBinaryLock} class="text-sky-800 dark:text-white/70" />
                        </button>
                      {/if}
                      {#if canLoadFileActions && entityType === 'study'}
                        <button
                          on:click={() => {
                            handleLoadEnrollmentLog(file);
                            handleMenuClose();
                          }}
                          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                          disabled={!isAwsConfigured || !canLoadFileActions}
                          class:opacity-50={!isAwsConfigured || !canLoadFileActions}
                          title={!isAwsConfigured ? 'AWS configuration missing' : (!canLoadFileActions ? 'Admin or support role required' : 'Load enrollment log')}
                        >
                          <span>Load Enrollment Log</span>
                          <FontAwesomeIcon icon={faFileImport} class="text-sky-800 dark:text-white/70" />
                        </button>
                      {/if}
                      {#if canLoadSensorData && entityType === 'participant'}
                        <button
                          on:click={() => {
                            openLoadConfigModal(file);
                            handleMenuClose();
                          }}
                          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                          disabled={!canManageFiles}
                          class:opacity-50={!canManageFiles}
                          title={!canManageFiles ? 'Insufficient permissions' : 'Load sensor data'}
                        >
                          <span>Load Sensor Data</span>
                          <FontAwesomeIcon icon={faFileImport} class="text-sky-800 dark:text-white/70" />
                        </button>
                      {/if}
                      {#if enableLegacySensorActions && entityType === 'participant' && fileType === FILE_TYPES.SENSOR.code && sensorLogsByKey[file.Key]?.status === 'imported' && (sensorLogsByKey[file.Key]?.inserted || 0) > 0}
                        <button
                          on:click={() => {
                            handleRemoveSensorData(file);
                            handleMenuClose();
                          }}
                          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                          disabled={!isAwsConfigured || !canManageFiles}
                          class:opacity-50={!isAwsConfigured || !canManageFiles}
                          title={!isAwsConfigured ? 'AWS configuration missing' : 'Remove sensor data'}
                          >
                          <span>Remove Sensor Data</span>
                          <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                        </button>
                      {/if}
                      <button
                        on:click={() => {
                          if (file._isExport) {
                            handleSiteExportDownload(file);
                          } else {
                            handleDownload(file);
                          }
                          handleMenuClose();
                        }}
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                        disabled={file._isExport ? (!canDownloadSiteExports || exportMeta?.status !== 'completed') : (!isAwsConfigured || !canDownloadFiles)}
                        class:opacity-50={file._isExport ? (!canDownloadSiteExports || exportMeta?.status !== 'completed') : (!isAwsConfigured || !canDownloadFiles)}
                        title={file._isExport
                          ? (!canDownloadSiteExports ? 'Insufficient permissions' : (exportMeta?.status !== 'completed' ? 'Export still processing' : 'Download export'))
                          : (!isAwsConfigured ? 'AWS configuration missing' : (!canDownloadFiles ? 'Insufficient permissions' : 'Download file'))}
                      >
                        <span>Download</span>
                        <FontAwesomeIcon icon={faCloudArrowDown} class="text-sky-800 dark:text-white/70" />
                      </button>
                      {#if canAdminFileActions && !file._isExport}
                        <button
                          on:click={() => {
                            handleCopyKey(file.Key);
                            handleMenuClose();
                          }}
                          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                          disabled={!canAdminFileActions}
                          class:opacity-50={!canAdminFileActions}
                          title={!canAdminFileActions ? 'Admin or support role required' : 'Copy S3 Key'}
                        >
                          <span>Copy S3 Key</span>
                          <FontAwesomeIcon icon={faClipboard} class="text-sky-800 dark:text-white/70" />
                        </button>
                      {/if}
                      {#if file._isExport && canDeleteSiteExports}
                        <button
                          on:click={() => {
                            handleDeleteSiteExport(file);
                            handleMenuClose();
                          }}
                          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                          disabled={!canDeleteSiteExports}
                          class:opacity-50={!canDeleteSiteExports}
                          title={!canDeleteSiteExports ? 'Admin role required' : 'Delete export'}
                        >
                          <span>Delete</span>
                          <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                        </button>
                      {/if}
                      {#if !file._isStudyFile && !file._isExport}
                        <button
                          on:click={() => {
                            handleDelete(file);
                            handleMenuClose();
                          }}
                          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                          disabled={!isAwsConfigured || !canLoadFileActions}
                          class:opacity-50={!isAwsConfigured || !canLoadFileActions}
                          title={!canLoadFileActions ? 'Admin, study, or support role required' : 'Delete file'}
                        >
                          <span>Delete</span>
                          <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                        </button>
                      {/if}
                    </div>
                  {/if}
                </div>
              </td>
            </tr>
          {/each}
        </tbody>
        
      </table>

      {#if totalPages > 1}
        <div class="flex justify-center items-center py-0">
          <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
        </div>
      {:else}
        <tfoot>
          <tr class="h-3">
            <td class="rounded-bl-lg" colspan="6"></td>
            <td class="rounded-br-lg"></td>
          </tr>
        </tfoot>
      {/if}
    </div>
  {/if}


  <!-- Upload Modal -->
  {#if showUploadModal}
    <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={closeUploadModal}></div>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div class="relative bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg max-w-md w-full mx-4 z-60" on:click|stopPropagation>
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Upload File</h3>
          <button
            class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            on:click={closeUploadModal}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div class="space-y-4">
          {#if showOverwriteWarning}
            <div class="p-0">
              <div class="flex items-start">
                <div class="flex-1 min-w-0">
                  <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">File Already Exists</h3>
                  <div class="mt-0 text-sm text-gray-800 dark:text-gray-200">
                    <p class="break-all">A file with the name <span class="font-mono font-semibold break-all">{conflictingFile?.filename}</span> already exists.</p>
                  </div>
                  <div class="mt-4">
                    <button
                      type="button"
                      on:click={handleOverwriteCancel}
                      class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Ok
                    </button>
                  </div>
                </div>
              </div>
            </div>
          {:else}
            <!-- Normal Upload Form -->
            <!-- File Selection -->
            <div>
              <!-- svelte-ignore a11y-label-has-associated-control -->
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Select File *
              </label>
              <div 
                class="relative"
                on:dragover={handleDragOver}
                on:dragleave={handleDragLeave}
                on:drop={handleDrop}
              >
                <input
                  type="file"
                  id="fileSelect"
                  bind:this={fileInput}
                  on:change={handleFileSelect}
                  class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <button
                  type="button"
                  class="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-dashed border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600 transition-colors duration-200 {isDragOver ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}"
                >
                  {#if isDragOver}
                    Drop file here
                  {:else if selectedFile}
                    {selectedFile.name}
                  {:else}
                    Choose File or Drag & Drop
                  {/if}
                </button>
              </div>
            </div>

            <!-- File Type Selection -->
            <div>
              <label for="fileType" class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                File Type *
              </label>
              <select
                id="fileType"
                bind:value={selectedUploadType}
                disabled={entityType === 'participant'}
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 {entityType === 'participant' ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}"
              >
                <option value="">Select file type</option>
                {#each Object.values(FILE_TYPES).filter(type => {
                  if (entityType === 'participant') {
                    return type.code === FILE_TYPES.SENSOR.code;
                  }
                  return type.code !== FILE_TYPES.ALL.code && type.code !== FILE_TYPES.SENSOR.code && type.code !== FILE_TYPES.EXPORTS.code;
                }) as type}
                  <option value={type.code}>{type.label}</option>
                {/each}
              </select>
              {#if entityType === 'participant'}
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Only sensor files are allowed for participants
                </p>
              {/if}
            </div>

            

            <!-- Preview of new filename -->
            {#if augmentFilenames && selectedFile && selectedUploadType}
              <div class="bg-gray-50 dark:bg-gray-800 p-3 rounded-md">
                <p class="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">New filename:</p>
                <p class="text-sm text-gray-600 dark:text-gray-400 font-mono">
                  {generatedFilename}
                </p>
              </div>
            {/if}

            <!-- Action Buttons -->
            <div class="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                on:click={closeUploadModal}
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                on:click={handleModalUpload}
                disabled={!selectedFile || !selectedUploadType || isLoading}
                class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          {/if}
        </div>
      </div>
    </div>
  {/if}

  {#if showLoadProgressModal}
    <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75"></div>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div class="relative bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg max-w-md w-full mx-4 z-60" on:click|stopPropagation>
        <div class="flex items-center gap-3">
          <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span class="text-sm text-gray-700 dark:text-gray-200">{loadProgressMessage}</span>
        </div>
      </div>
    </div>
  {/if}

  {#if showLoadResultModal}
    <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={() => { showLoadResultModal = false; }}></div>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div class="relative bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg max-w-2xl w-full mx-4 z-60" on:click|stopPropagation>
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Load Sensor Data Result</h3>
          <button
            class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            on:click={() => { showLoadResultModal = false; }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div class="space-y-3">
          {#if loadResultSummary}
            <div class="text-sm text-gray-800 dark:text-gray-200">
              <span on:click={() => { showLoadRaw = !showLoadRaw; }} class="cursor-pointer hover:underline">
                {loadResultSummary}
              </span>
            </div>
          {/if}
          {#if loadResultError}
            <div class="text-sm text-red-700 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
              <span on:click={() => { showLoadRaw = !showLoadRaw; }} class="cursor-pointer hover:underline">
                {loadResultError}
              </span>
            </div>
          {/if}
          {#if showLoadRaw}
            <div class="space-y-2">
              <!-- <div class="text-xs text-gray-600 dark:text-gray-300 break-all"><span class="font-semibold">URL:</span> {loadRequestUrl}</div> -->
              <div>
                <div class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Payload</div>
                <pre class="text-xs bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 p-3 rounded overflow-auto max-h-60">
{JSON.stringify(loadRequestPayload, null, 2)}
                </pre>
              </div>
              <div>
                <div class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Response</div>
                <pre class="text-xs bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 p-3 rounded overflow-auto max-h-60">
{JSON.stringify(loadResultData, null, 2)}
                </pre>
              </div>
            </div>
          {/if}
        </div>

        <div class="mt-4 flex justify-end">
          <button
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            on:click={() => { showLoadResultModal = false; }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  {/if}

</div> 